import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { formatUsDate } from "../utils/completeByDate";
import DatePickerField from "./DatePickerField";
import {
    createNilDeal,
    getNilDealAgreementUrl,
    NilDealDto,
    updateNilDeal,
} from "../services/nilDealsService";
import "../styles/nil-deals.css";
import "../styles/file-upload-modal.css";

const UploadIcon = () => (
    <svg className="fum-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

const FileIcon = () => (
    <svg className="fum-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);

const CloseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

function formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export type NilDealModalMode = "create" | "edit" | "view";

type Props = {
    open: boolean;
    mode: NilDealModalMode;
    deal?: NilDealDto | null;
    athleteUserId?: number | null;
    onClose: () => void;
    onSaved: (deal: NilDealDto) => void;
};

const ACCEPT =
    ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.odt,.ods,.odp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function daysInMonth(year: number, month: number): number {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Parses MM/DD/YYYY (or ISO) into an ISO date. Past dates are allowed (deals are historical). */
function parseUsDateToIso(raw: string): { isoDate: string } | { error: string } {
    const value = raw.trim();
    if (!value) {
        return { error: "Date submitted is required" };
    }
    let year: number;
    let month: number;
    let day: number;
    const usMatch = US_DATE.exec(value);
    const isoMatch = ISO_DATE.exec(value);
    if (usMatch) {
        month = Number(usMatch[1]);
        day = Number(usMatch[2]);
        year = Number(usMatch[3]);
    } else if (isoMatch) {
        year = Number(isoMatch[1]);
        month = Number(isoMatch[2]);
        day = Number(isoMatch[3]);
    } else {
        return { error: "Enter a valid date (MM/DD/YYYY)" };
    }
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
        return { error: "Enter a valid date (MM/DD/YYYY)" };
    }
    const isoDate = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return { isoDate };
}

export default function NilDealModal({ open, mode, deal, athleteUserId, onClose, onSaved }: Props) {
    const [dealName, setDealName] = useState("");
    const [description, setDescription] = useState("");
    const [dateText, setDateText] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [existingAgreementName, setExistingAgreementName] = useState<string | null>(null);
    const [hasExistingAgreement, setHasExistingAgreement] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{ dealName?: string; description?: string; date?: string }>({});
    const [progress, setProgress] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const readOnly = mode === "view";

    // Reset form when opened / when the target deal changes.
    useEffect(() => {
        if (!open) {
            return;
        }
        setDealName(deal?.dealName ?? "");
        setDescription(deal?.description ?? "");
        setDateText(deal?.dateSubmitted ? formatUsDate(deal.dateSubmitted) : "");
        setFile(null);
        setExistingAgreementName(deal?.agreementFileName ?? null);
        setHasExistingAgreement(Boolean(deal?.hasAgreement));
        setError(null);
        setFieldErrors({});
        setProgress(null);
        setSubmitting(false);
        setDragOver(false);
    }, [open, deal]);

    // Escape closes (discard). Backdrop click intentionally does NOT close.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (open && e.key === "Escape" && !submitting) {
                onClose();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose, submitting]);

    const title = useMemo(() => {
        if (mode === "view") return "NIL Deal";
        if (mode === "edit") return "Edit NIL Deal";
        return "Add NIL Deal";
    }, [mode]);

    if (!open) {
        return null;
    }

    const validate = (): { dealName: string; description: string; isoDate: string } | null => {
        const errs: { dealName?: string; description?: string; date?: string } = {};
        const name = dealName.trim();
        const desc = description.trim();
        if (!name) {
            errs.dealName = "Deal name is required";
        } else if (name.length > 100) {
            errs.dealName = "Deal name must be 100 characters or fewer";
        }
        if (!desc) {
            errs.description = "Description is required";
        } else if (desc.length > 2000) {
            errs.description = "Description must be 2000 characters or fewer";
        }
        const parsed = parseUsDateToIso(dateText);
        let isoDate = "";
        if ("error" in parsed) {
            errs.date = parsed.error;
        } else {
            isoDate = parsed.isoDate;
        }
        setFieldErrors(errs);
        if (Object.keys(errs).length > 0) {
            return null;
        }
        return { dealName: name, description: desc, isoDate };
    };

    const pickFile = (f: File | null | undefined) => {
        if (f) {
            setFile(f);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        pickFile(e.dataTransfer.files?.[0]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (readOnly) {
            return;
        }
        const valid = validate();
        if (!valid) {
            return;
        }
        setError(null);
        setSubmitting(true);
        // Show the progress bar from the start only when a file is being uploaded.
        setProgress(file ? 0 : null);
        try {
            const payload = {
                dealName: valid.dealName,
                description: valid.description,
                dateSubmitted: valid.isoDate,
            };
            // The agreement (if any) is streamed to our API, virus scanned server-side, and only
            // then stored in S3. onProgress drives the upload progress bar for large documents.
            const onProgress = file ? setProgress : undefined;

            const saved =
                mode === "edit" && deal
                    ? await updateNilDeal(deal.id, payload, file, athleteUserId, onProgress)
                    : await createNilDeal(payload, file, athleteUserId, onProgress);
            onSaved(saved);
            onClose();
        } catch (err: any) {
            setError(err?.message || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
            setProgress(null);
        }
    };

    const handleViewDocument = async () => {
        if (!deal) {
            return;
        }
        try {
            const { url } = await getNilDealAgreementUrl(deal.id, athleteUserId);
            window.open(url, "_blank", "noopener,noreferrer");
        } catch {
            setError("We couldn’t open the agreement document. Please try again.");
        }
    };

    const clearFile = () => {
        if (!submitting) {
            setFile(null);
        }
    };

    return createPortal(
        <div className="nil-deal-modal-overlay" data-testid="nil-deal-modal">
            <div
                className="nil-deal-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="nil-deal-modal-title"
            >
                <div className="nil-deal-modal-header">
                    <h2 id="nil-deal-modal-title">{title}</h2>
                    <button
                        type="button"
                        className="nil-deal-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="nil-deal-modal-close"
                    >
                        ×
                    </button>
                </div>

                <form className="nil-deal-modal-body" onSubmit={handleSubmit}>
                    <div className="nil-deal-field">
                        <label htmlFor="nil-deal-name">Deal Name</label>
                        <input
                            id="nil-deal-name"
                            type="text"
                            value={dealName}
                            onChange={(e) => setDealName(e.target.value)}
                            disabled={readOnly || submitting}
                            maxLength={100}
                            autoComplete="off"
                            data-testid="nil-deal-name-input"
                        />
                        {fieldErrors.dealName && (
                            <div className="nil-deal-field-error" role="alert">{fieldErrors.dealName}</div>
                        )}
                    </div>

                    <div className="nil-deal-field">
                        <label htmlFor="nil-deal-description">Description</label>
                        <textarea
                            id="nil-deal-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={readOnly || submitting}
                            maxLength={2000}
                            rows={4}
                            data-testid="nil-deal-description-input"
                        />
                        {fieldErrors.description && (
                            <div className="nil-deal-field-error" role="alert">{fieldErrors.description}</div>
                        )}
                    </div>

                    <div className="nil-deal-field">
                        <label htmlFor="nil-deal-date">Date Submitted</label>
                        <DatePickerField
                            inputId="nil-deal-date"
                            inputTestId="nil-deal-date-input"
                            ariaLabel="Date submitted"
                            value={dateText}
                            onChange={setDateText}
                            disabled={readOnly || submitting}
                        />
                        {fieldErrors.date && (
                            <div className="nil-deal-field-error" role="alert">{fieldErrors.date}</div>
                        )}
                    </div>

                    <div className="nil-deal-field">
                        <label>Agreement <span className="nil-deal-optional">(optional)</span></label>
                        {readOnly ? (
                            hasExistingAgreement ? (
                                <button
                                    type="button"
                                    className="nil-deal-view-document-link"
                                    onClick={handleViewDocument}
                                    data-testid="nil-deal-view-document"
                                >
                                    View document
                                </button>
                            ) : (
                                <span className="nil-deal-no-link">No agreement uploaded</span>
                            )
                        ) : (
                            <div className="nil-deal-upload">
                                {file ? (
                                    <div className="fum-dropzone fum-has-file">
                                        <div className="fum-file-preview">
                                            <FileIcon />
                                            <div className="fum-file-info">
                                                <span className="fum-file-name">{file.name}</span>
                                                <span className="fum-file-size">{formatFileSize(file.size)}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="fum-icon-btn"
                                                onClick={clearFile}
                                                disabled={submitting}
                                                aria-label="Remove file"
                                            >
                                                <CloseIcon />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <label
                                        htmlFor="nil-deal-file"
                                        className={`fum-dropzone ${dragOver ? "fum-drag-over" : ""}`}
                                        onDragEnter={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDragOver(true);
                                        }}
                                        onDragLeave={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDragOver(false);
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onDrop={handleDrop}
                                    >
                                        <UploadIcon />
                                        <p>
                                            <strong>Click to upload</strong> or drag and drop
                                        </p>
                                        <p className="fum-dropzone-hint">
                                            PDF, Word, Excel, PowerPoint, OpenDocument, text, RTF, or CSV — one file at a time.
                                        </p>
                                    </label>
                                )}
                                {/* Sibling of the dropzone (visually hidden, not display:none): a nested
                                    input.click() from a label/div click is suppressed by some browsers. */}
                                <input
                                    id="nil-deal-file"
                                    type="file"
                                    accept={ACCEPT}
                                    className="fum-file-input"
                                    onChange={(e) => pickFile(e.target.files?.[0])}
                                    disabled={submitting}
                                    data-testid="nil-deal-file-input"
                                />
                                {!file && hasExistingAgreement && existingAgreementName && (
                                    <div className="nil-deal-current-file">
                                        <FileIcon />
                                        <span>
                                            Current file: <strong>{existingAgreementName}</strong> — choose a new file to replace it.
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {progress != null && (
                        <div className="nil-deal-progress">
                            <div className="nil-deal-progress-bar">
                                <div className="nil-deal-progress-fill" style={{ width: `${progress}%` }} />
                            </div>
                            <div className="nil-deal-progress-label">{progress}%</div>
                        </div>
                    )}

                    {error && (
                        <div className="nil-deal-error" role="alert" data-testid="nil-deal-error">
                            {error}
                        </div>
                    )}

                    <div className="nil-deal-modal-actions">
                        {readOnly ? (
                            <button type="button" className="nil-deal-btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="nil-deal-btn-secondary"
                                    onClick={onClose}
                                    disabled={submitting}
                                    data-testid="nil-deal-cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="nil-deal-btn-primary"
                                    disabled={submitting}
                                    data-testid="nil-deal-save"
                                >
                                    {submitting ? "Saving…" : "Save"}
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}
